import React, { useEffect, useMemo, useState } from "react";
import io from "socket.io-client";
import {
  Table,
  Space,
  Tag,
  Grid,
  Input,
  Typography,
  List,
  Card,
  Button,
  Modal,
  message,
  InputNumber,
  Popconfirm,
  Select,
  DatePicker,
  Spin,
  Avatar,
  Tooltip,
} from "antd";
import {
  CalendarOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  ClockCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { API_BASE, SOCKET_URL } from "../../config/api";
import "dayjs/locale/es";
import "./Reservations.css";

dayjs.locale("es");

const { useBreakpoint } = Grid;
const { Search } = Input;
const { Title, Text } = Typography;

const getReservationGroupKey = (reservation) => {
  if (reservation.comboGroupId) {
    return `combo-${reservation.comboGroupId}`;
  }

  return `booking-${reservation.id}`;
};

const groupReservations = (reservations) => {
  const grouped = new Map();

  reservations.forEach((reservation) => {
    const key = getReservationGroupKey(reservation);
    const currentGroup = grouped.get(key);

    if (currentGroup) {
      currentGroup.items.push(reservation);
      currentGroup.itemIds.push(reservation.id);
      currentGroup.totalPrice += Number(reservation.service?.price || 0);
      return;
    }

    grouped.set(key, {
      key,
      itemIds: [reservation.id],
      items: [reservation],
      user: reservation.user,
      date: reservation.date,
      time: reservation.time,
      status: reservation.status,
      paid: reservation.paid,
      duration: reservation.duration || reservation.service?.duration || 60,
      totalPrice: Number(reservation.service?.price || 0),
      combo: Boolean(
        reservation.comboGroupId || (reservation.comboServiceIds?.length || 0) > 1
      ),
    });
  });

  return Array.from(grouped.values());
};

const getStatusColor = (status) => {
  switch (status) {
    case "reservado":
    case "pendiente":
    case "pending":
    case "confirmado":
    case "confirmed":
      return "green";
    case "en_curso":
      return "blue";
    case "cancelado":
    case "cancelled":
      return "red";
    case "finalizado":
      return "default";
    default:
      return "blue";
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case "reservado":
      return "Reservado";
    case "pendiente":
    case "pending":
      return "Reservado";
    case "confirmado":
    case "confirmed":
      return "Reservado";
    case "en_curso":
      return "En curso";
    case "cancelado":
    case "cancelled":
      return "Cancelada";
    case "finalizado":
      return "Finalizada";
    default:
      return status || "Reservado";
  }
};

const STATUS_OPTIONS = [
  { value: "reservado", label: "Reservado" },
  { value: "en_curso", label: "En curso" },
  { value: "finalizado", label: "Finalizada" },
  { value: "cancelado", label: "Cancelada" },
];

const getGroupSearchText = (group) => {
  const fullName = `${group.user?.name || ""} ${group.user?.lastName || ""}`;
  const email = group.user?.email || "";
  const services = group.items
    .map((item) => item.service?.name || "")
    .join(" ");
  const employees = group.items
    .map((item) => item.employee?.name || "")
    .join(" ");

  return `${fullName} ${email} ${services} ${employees} ${group.date || ""}`.toLowerCase();
};

const renderPaymentStatus = (record) => {
  if (record.user?.type_client === "new") {
    return (
      <Tag color="green">
        Pago ${record.totalPrice} de la reservacion
      </Tag>
    );
  }

  if (record.user?.type_client === "reliable") {
    return (
      <Tag color="orange">
        DEBE PAGAR
      </Tag>
    );
  }

  return <Tag>—</Tag>;
};

const Reservations = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const location = useLocation();
  const navigate = useNavigate();

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [editingDuration, setEditingDuration] = useState({});
  const [editingStatus, setEditingStatus] = useState({});
  const [deletingKey, setDeletingKey] = useState("");
  const [updatingStatusKey, setUpdatingStatusKey] = useState("");
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [editingReservation, setEditingReservation] = useState(null);
  const [editDate, setEditDate] = useState(null);
  const [editTime, setEditTime] = useState(null);
  const [editSelections, setEditSelections] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [savingReservation, setSavingReservation] = useState(false);

  useEffect(() => {
    fetchReservations();
    fetchCatalogs();
  }, []);

  const fetchCatalogs = async () => {
    try {
      const [servicesResponse, categoriesResponse, employeesResponse] = await Promise.all([
        axios.get(`${API_BASE}/services`),
        axios.get(`${API_BASE}/category`),
        axios.get(`${API_BASE}/employees`),
      ]);
      setServices(Array.isArray(servicesResponse.data) ? servicesResponse.data : []);
      setCategories(Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []);
      setEmployees(Array.isArray(employeesResponse.data) ? employeesResponse.data : []);
    } catch (error) {
      message.error("No se pudieron cargar servicios y profesionales");
    }
  };

  useEffect(() => {
    const socket = io(SOCKET_URL);

    const handleBookingsUpdate = () => {
      fetchReservations();
    };

    const handleAvailabilityUpdate = () => {
      fetchReservations();
    };

    socket.on("bookings:updated", handleBookingsUpdate);
    socket.on("availability:updated", handleAvailabilityUpdate);

    return () => {
      socket.off("bookings:updated", handleBookingsUpdate);
      socket.off("availability:updated", handleAvailabilityUpdate);
      socket.disconnect();
    };
  }, []);

  const fetchReservations = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${API_BASE}/booking`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setReservations(Array.isArray(res.data) ? res.data : []);
      setEditingDuration({});
      setEditingStatus({});
    } catch (error) {
      Modal.error({
        title: "Error al cargar reservas",
        content: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateDuration = async (ids, duration, fallbackDuration) => {
    try {
      const targetIds = Array.isArray(ids) ? ids : [ids];
      const nextDuration = duration || fallbackDuration;

      if (!nextDuration || typeof nextDuration !== "number") {
        return message.error("Duración inválida");
      }

      if (nextDuration < 15 || nextDuration > 720) {
        return message.error("Debe estar entre 15 y 720 minutos");
      }

      if (nextDuration % 15 !== 0) {
        return message.error("La duración debe ser múltiplo de 15 (15, 30, 45...)");
      }

      const token = localStorage.getItem("token");

      await Promise.all(
        targetIds.map((id) =>
          axios.patch(
            `${API_BASE}/booking/${id}/duration`,
            { duration: nextDuration },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );

      message.success("Duración actualizada");
      fetchReservations();
    } catch (error) {
      Modal.error({
        title: "Error al actualizar duración",
        content: error.response?.data?.message || error.message,
      });
    }
  };

  const deleteReservation = async (record) => {
    try {
      setDeletingKey(record.key);
      const token = localStorage.getItem("token");

      await axios.delete(`${API_BASE}/booking/${record.itemIds[0]}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      message.success(record.combo ? "Reserva combinada eliminada" : "Reserva eliminada");
      fetchReservations();
    } catch (error) {
      Modal.error({
        title: "Error al eliminar reserva",
        content: error.response?.data?.message || error.message,
      });
    } finally {
      setDeletingKey("");
    }
  };

  const updateStatus = async (record, nextStatus) => {
    try {
      const targetStatus = nextStatus ?? editingStatus[record.key] ?? record.status;

      if (!targetStatus || targetStatus === record.status) {
        return;
      }

      setUpdatingStatusKey(record.key);
      setEditingStatus((prev) => ({
        ...prev,
        [record.key]: targetStatus,
      }));
      const token = localStorage.getItem("token");

      await axios.patch(
        `${API_BASE}/booking/${record.itemIds[0]}/status`,
        { status: targetStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success("Estado actualizado");
      fetchReservations();
    } catch (error) {
      setEditingStatus((prev) => ({
        ...prev,
        [record.key]: record.status,
      }));
      Modal.error({
        title: "Error al actualizar estado",
        content: error.response?.data?.message || error.message,
      });
    } finally {
      setUpdatingStatusKey("");
    }
  };

  const getServiceHierarchy = (serviceId) => {
    for (const category of categories) {
      for (const subCategory of category.SubCategories || []) {
        const service = (subCategory.Services || []).find(
          (item) => Number(item.id) === Number(serviceId)
        );
        if (service) {
          return {
            categoryId: Number(category.id),
            subCategoryId: Number(subCategory.id),
          };
        }
      }
    }
    return null;
  };

  const openEditReservation = (record) => {
    setEditingReservation(record);
    setEditDate(dayjs(record.date));
    setEditTime(String(record.time || "").slice(0, 5));
    setEditSelections(record.items.map((item) => {
      const serviceId = Number(item.service?.id || item.serviceId);
      const service = services.find((current) => Number(current.id) === serviceId);
      const hierarchy = getServiceHierarchy(serviceId);
      return {
        bookingId: item.id,
        categoryId:
          hierarchy?.categoryId || Number(service?.SubCategory?.categoryId || 0) || null,
        subCategoryId:
          hierarchy?.subCategoryId ||
          Number(service?.subCategoryId || service?.SubCategory?.id || 0) ||
          null,
        serviceId,
        employeeId: Number(item.employee?.id || item.employeeId),
      };
    }));
    setAvailableSlots([]);
  };

  const updateEditSelection = (bookingId, changes) => {
    setEditSelections((current) =>
      current.map((selection) =>
        String(selection.bookingId) === String(bookingId)
          ? { ...selection, ...changes }
          : selection
      )
    );
    setEditTime(null);
  };

  const getEmployeeOptions = (serviceId) => {
    const service = services.find((item) => Number(item.id) === Number(serviceId));
    const assignedEmployees = Array.isArray(service?.employees)
      ? service.employees.filter((employee) => employee.active !== false)
      : [];
    const source = assignedEmployees.length ? assignedEmployees : employees;
    return source.map((employee) => ({
      value: Number(employee.id),
      label: employee.name,
    }));
  };

  const getSubCategoryOptions = (categoryId) => {
    const category = categories.find((item) => Number(item.id) === Number(categoryId));
    return (category?.SubCategories || []).map((subCategory) => ({
      value: Number(subCategory.id),
      label: subCategory.name,
    }));
  };

  const getServiceOptions = (categoryId, subCategoryId) => {
    const category = categories.find((item) => Number(item.id) === Number(categoryId));
    const subCategory = (category?.SubCategories || []).find(
      (item) => Number(item.id) === Number(subCategoryId)
    );
    return (subCategory?.Services || []).map((service) => ({
      value: Number(service.id),
      label: `${service.name} · $${Number(service.price || 0)}`,
    }));
  };

  useEffect(() => {
    if (!editingReservation || !editDate || !editSelections.length) return;
    if (editSelections.some((selection) => !selection.serviceId || !selection.employeeId)) {
      setAvailableSlots([]);
      return;
    }

    let active = true;
    const fetchAvailableSlots = async () => {
      setLoadingSlots(true);
      try {
        const token = localStorage.getItem("token");
        const params = new URLSearchParams({
          date: editDate.format("YYYY-MM-DD"),
          comboSelections: editSelections
            .map((selection) => `${selection.serviceId}:${selection.employeeId}`)
            .join(","),
          rescheduleBookingId: String(editingReservation.itemIds[0]),
        });
        const response = await axios.get(
          `${API_BASE}/timeSlot/available?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (active) {
          setAvailableSlots(
            (Array.isArray(response.data) ? response.data : []).filter(
              (slot) => slot.available
            )
          );
        }
      } catch (error) {
        if (active) {
          setAvailableSlots([]);
          message.error(error.response?.data?.message || "No se pudieron cargar los horarios");
        }
      } finally {
        if (active) setLoadingSlots(false);
      }
    };

    fetchAvailableSlots();
    return () => {
      active = false;
    };
  }, [editingReservation, editDate, editSelections]);

  const saveEditedReservation = async () => {
    if (!editingReservation || !editDate || !editTime) {
      message.warning("Completá servicio, profesional, fecha y horario");
      return;
    }

    setSavingReservation(true);
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API_BASE}/admin/booking/${editingReservation.itemIds[0]}`,
        {
          date: editDate.format("YYYY-MM-DD"),
          time: editTime,
          selections: editSelections,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Reserva actualizada correctamente");
      setEditingReservation(null);
      const currentParams = new URLSearchParams(location.search);
      if (currentParams.has("date") || currentParams.has("time") || currentParams.has("employeeId")) {
        const primarySelection = editSelections[0];
        const selectedEmployee = employees.find(
          (employee) => Number(employee.id) === Number(primarySelection?.employeeId)
        );
        currentParams.set("date", editDate.format("YYYY-MM-DD"));
        currentParams.set("time", editTime);
        currentParams.set("employeeId", String(primarySelection.employeeId));
        if (selectedEmployee?.name) currentParams.set("employeeName", selectedEmployee.name);
        navigate({ search: currentParams.toString() }, { replace: true });
      }
      await fetchReservations();
    } catch (error) {
      Modal.error({
        title: "No se pudo actualizar la reserva",
        content: error.response?.data?.message || error.message,
      });
    } finally {
      setSavingReservation(false);
    }
  };

  const groupedReservations = useMemo(() => {
    const groups = groupReservations(reservations);
    const params = new URLSearchParams(location.search);
    const date = params.get("date");
    const time = params.get("time");
    const employeeId = Number(params.get("employeeId") || 0);
    const normalizedSearch = (searchText || "").trim().toLowerCase();

    return groups.filter((group) => {
      const matchDate = date ? group.date === date : true;
      const matchTime = time ? group.time === time : true;
      const matchEmployee = employeeId
        ? group.items.some((item) => Number(item.employee?.id) === employeeId)
        : true;
      const matchSearch = normalizedSearch
        ? getGroupSearchText(group).includes(normalizedSearch)
        : true;

      return matchDate && matchTime && matchEmployee && matchSearch;
    });
  }, [location.search, reservations, searchText]);

  const activeFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      date: params.get("date"),
      time: params.get("time"),
      employeeName: params.get("employeeName"),
    };
  }, [location.search]);

  const columns = [
    {
      title: "Cliente",
      render: (_, record) => (
        <Space size={11}>
          <Avatar
            size={38}
            style={{ background: "#ede9fe", color: "#6d28d9", fontWeight: 700 }}
          >
            {(record.user?.name || "C").slice(0, 1).toUpperCase()}
          </Avatar>
          <div>
            <Text strong style={{ display: "block" }}>
              {record.user?.name} {record.user?.lastName}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.user?.email || "Sin email"}
            </Text>
            {record.combo && <Tag color="purple" style={{ marginTop: 4 }}>Combinado</Tag>}
          </div>
        </Space>
      ),
      width: 230,
    },
    {
      title: "Empleados",
      render: (_, record) => (
        <Space wrap>
          {record.items.map((item) => (
            <Tag key={`employee-${record.key}-${item.id}`} color={item.employee?.background_color}>
              {item.employee?.name} {item.employee?.lastName}
            </Tag>
          ))}
        </Space>
      ),
      width: 170,
    },
    {
      title: "Servicios",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.items.map((item) => (
            <Text key={`service-${record.key}-${item.id}`}>{item.service?.name}</Text>
          ))}
        </Space>
      ),
      width: 190,
    },
    {
      title: "Pago",
      key: "pago",
      render: (_, record) => renderPaymentStatus(record),
      width: 150,
    },
    {
      title: "Fecha",
      render: (_, record) => dayjs(record.date).format("DD/MM/YYYY"),
      width: 115,
    },
    {
      title: "Hora",
      dataIndex: "time",
      width: 82,
    },
    {
      title: "Duracion (min)",
      render: (_, record) => (
        <Space>
          <InputNumber
            min={15}
            max={720}
            step={15}
            controls
            keyboard={false}
            value={editingDuration[record.key] ?? record.duration}
            onChange={(value) =>
              setEditingDuration((prev) => ({
                ...prev,
                [record.key]: value,
              }))
            }
          />

          <Button
            type="primary"
            size="small"
            onClick={() =>
              updateDuration(
                record.itemIds,
                editingDuration[record.key],
                record.duration
              )
            }
          >
            Guardar
          </Button>
        </Space>
      ),
      width: 205,
    },
    {
      title: "Estado",
      render: (_, record) => (
        <Space direction="vertical" size={10}>
          <Tag color={getStatusColor(record.status)}>
            {getStatusLabel(record.status)}
          </Tag>

          <Select
            style={{ minWidth: 170 }}
            value={editingStatus[record.key] ?? record.status}
            options={STATUS_OPTIONS}
            loading={updatingStatusKey === record.key}
            disabled={updatingStatusKey === record.key}
            onChange={(value) => updateStatus(record, value)}
          />
        </Space>
      ),
      width: 190,
    },
    {
      title: "Acciones",
      render: (_, record) => (
        <Space wrap>
          <Tooltip title="Cambiar servicio, profesional, fecha u horario">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => openEditReservation(record)}
            >
              Editar
            </Button>
          </Tooltip>
          <Popconfirm
            title={record.combo ? "¿Eliminar toda la reserva combinada?" : "¿Eliminar reserva?"}
            onConfirm={() => deleteReservation(record)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={deletingKey === record.key}
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
      width: 190,
    },
  ];

  const renderMobileCard = (item) => (
    <Card
      key={item.key}
      style={{ marginBottom: 16 }}
      title={`${item.user?.name || ""} ${item.user?.lastName || ""}`}
      extra={<Tag color={getStatusColor(item.status)}>{getStatusLabel(item.status)}</Tag>}
    >
      {item.combo && (
        <div style={{ marginBottom: 10 }}>
          <Tag color="purple">Combinado</Tag>
        </div>
      )}

      <p><strong>Servicios:</strong></p>
      <Space direction="vertical" size={2} style={{ marginBottom: 10 }}>
        {item.items.map((reservation) => (
          <Text key={`mobile-service-${item.key}-${reservation.id}`}>
            {reservation.service?.name} - {reservation.employee?.name}
          </Text>
        ))}
      </Space>

      <p><strong>Precio:</strong> ${item.totalPrice}</p>
      <p><strong>Fecha:</strong> {dayjs(item.date).format("DD/MM/YYYY")}</p>
      <p><strong>Hora:</strong> {item.time}</p>
      <p><strong>Duracion:</strong> {item.duration} min</p>
      <p>
        <strong>Estado:</strong>{" "}
        <Tag color={getStatusColor(item.status)}>{getStatusLabel(item.status)}</Tag>
      </p>

      <Space style={{ marginTop: 10 }}>
        <InputNumber
          min={15}
          max={720}
          step={15}
          controls
          keyboard={false}
          value={editingDuration[item.key] ?? item.duration}
          onChange={(value) =>
            setEditingDuration((prev) => ({
              ...prev,
              [item.key]: value,
            }))
          }
        />

        <Button
          type="primary"
          size="small"
          onClick={() =>
            updateDuration(
              item.itemIds,
              editingDuration[item.key],
              item.duration
            )
          }
        >
          Guardar
        </Button>
      </Space>

      <Space style={{ marginTop: 10 }} wrap>
        <Select
          style={{ minWidth: 150 }}
          value={editingStatus[item.key] ?? item.status}
          options={STATUS_OPTIONS}
          loading={updatingStatusKey === item.key}
          disabled={updatingStatusKey === item.key}
          onChange={(value) => updateStatus(item, value)}
        />
      </Space>

      <div style={{ marginTop: 10 }}>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => openEditReservation(item)}
          style={{ marginRight: 8 }}
        >
          Editar reserva
        </Button>
        <Popconfirm
          title={item.combo ? "¿Eliminar toda la reserva combinada?" : "¿Eliminar reserva?"}
          onConfirm={() => deleteReservation(item)}
          okText="Sí"
          cancelText="No"
        >
          <Button
            danger
            icon={<DeleteOutlined />}
            loading={deletingKey === item.key}
          >
            Eliminar
          </Button>
        </Popconfirm>
      </div>

      <div style={{ marginTop: 10 }}>
        {renderPaymentStatus(item)}
      </div>
    </Card>
  );

  return (
    <div className="reservations-page">
      <Card bordered={false} className="reservations-hero">
        <div className="reservations-hero-content">
          <Space align="start" size={14}>
            <Button
              shape="circle"
              size="large"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
            />
            <div>
              <Text className="reservations-kicker">AGENDA DEL SALÓN</Text>
              <Title level={2} style={{ margin: "4px 0 5px", color: "#0f172a" }}>
                Gestión de reservas
              </Title>
              <Text type="secondary">
                Consultá, editá y administrá las reservas del horario seleccionado.
              </Text>
            </div>
          </Space>

          <Space wrap size={[8, 8]}>
            {activeFilters.employeeName && (
              <Tag icon={<UserOutlined />} color="purple" className="reservations-filter-tag">
                {activeFilters.employeeName}
              </Tag>
            )}
            {activeFilters.date && (
              <Tag icon={<CalendarOutlined />} color="blue" className="reservations-filter-tag">
                {dayjs(activeFilters.date).format("DD/MM/YYYY")}
              </Tag>
            )}
            {activeFilters.time && (
              <Tag icon={<ClockCircleOutlined />} color="cyan" className="reservations-filter-tag">
                {activeFilters.time}
              </Tag>
            )}
          </Space>
        </div>
      </Card>

      <Card bordered={false} className="reservations-table-card">
        <div className="reservations-toolbar">
          <div>
            <Text strong style={{ display: "block", fontSize: 16, color: "#0f172a" }}>
              Reservas encontradas
            </Text>
            <Text type="secondary">
              {groupedReservations.length} {groupedReservations.length === 1 ? "reserva" : "reservas"}
            </Text>
          </div>
          <Search
            placeholder="Buscar cliente, servicio o fecha"
            allowClear
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={setSearchText}
            style={{ width: isMobile ? "100%" : 380 }}
          />
        </div>

        {isMobile ? (
          <div className="reservations-mobile-list">
            <List
              dataSource={groupedReservations}
              loading={loading}
              renderItem={renderMobileCard}
            />
          </div>
        ) : (
          <Table
            className="reservations-table"
            dataSource={groupedReservations}
            columns={columns}
            rowKey="key"
            loading={loading}
            size="middle"
            tableLayout="fixed"
            scroll={{ x: 1522 }}
            pagination={{ pageSize: 10, showSizeChanger: false }}
          />
        )}
      </Card>

      <Modal
        open={Boolean(editingReservation)}
        title={editingReservation?.combo ? "Editar reserva combinada" : "Editar reserva"}
        okText="Guardar cambios"
        cancelText="Cancelar"
        width={760}
        confirmLoading={savingReservation}
        onOk={saveEditedReservation}
        onCancel={() => setEditingReservation(null)}
        destroyOnHidden
      >
        <Text type="secondary">
          Podés cambiar servicios, profesionales, fecha y horario. La disponibilidad
          se valida antes de guardar.
        </Text>

        <Space direction="vertical" size={16} style={{ width: "100%", marginTop: 20 }}>
          {editSelections.map((selection, index) => (
            <Card
              key={selection.bookingId}
              size="small"
              title={editSelections.length > 1 ? `Servicio ${index + 1}` : "Servicio y profesional"}
              style={{ borderRadius: 12 }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "minmax(0, 1fr)"
                    : "repeat(2, minmax(0, 1fr))",
                  gap: 14,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <Text strong>Categoría</Text>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    value={selection.categoryId}
                    placeholder="Seleccioná una categoría"
                    style={{ width: "100%", marginTop: 7 }}
                    size="large"
                    options={categories.map((category) => ({
                      value: Number(category.id),
                      label: category.name,
                    }))}
                    onChange={(categoryId) =>
                      updateEditSelection(selection.bookingId, {
                        categoryId,
                        subCategoryId: null,
                        serviceId: null,
                        employeeId: null,
                      })
                    }
                  />
                </div>
                <div style={{ minWidth: 0 }}>
                  <Text strong>Subcategoría</Text>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    value={selection.subCategoryId}
                    placeholder="Seleccioná una subcategoría"
                    disabled={!selection.categoryId}
                    style={{ width: "100%", marginTop: 7 }}
                    size="large"
                    options={getSubCategoryOptions(selection.categoryId)}
                    onChange={(subCategoryId) =>
                      updateEditSelection(selection.bookingId, {
                        subCategoryId,
                        serviceId: null,
                        employeeId: null,
                      })
                    }
                  />
                </div>
                <div style={{ minWidth: 0 }}>
                  <Text strong>Servicio</Text>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    value={selection.serviceId}
                    placeholder="Seleccioná un servicio"
                    disabled={!selection.subCategoryId}
                    style={{ width: "100%", marginTop: 7 }}
                    size="large"
                    options={getServiceOptions(
                      selection.categoryId,
                      selection.subCategoryId
                    )}
                    onChange={(serviceId) =>
                      updateEditSelection(selection.bookingId, {
                        serviceId,
                        employeeId: null,
                      })
                    }
                  />
                </div>
                <div style={{ minWidth: 0 }}>
                  <Text strong>Profesional</Text>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    value={selection.employeeId}
                    placeholder="Seleccioná un profesional"
                    disabled={!selection.serviceId}
                    style={{ width: "100%", marginTop: 7 }}
                    size="large"
                    options={getEmployeeOptions(selection.serviceId)}
                    onChange={(employeeId) =>
                      updateEditSelection(selection.bookingId, { employeeId })
                    }
                  />
                </div>
              </div>
            </Card>
          ))}

          <Card size="small" title="Nueva fecha y horario" style={{ borderRadius: 12 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "minmax(0, 1fr)"
                  : "repeat(2, minmax(0, 1fr))",
                gap: 14,
              }}
            >
              <div>
                <Text strong>Fecha</Text>
                <DatePicker
                  value={editDate}
                  format="DD/MM/YYYY"
                  size="large"
                  style={{ width: "100%", marginTop: 7 }}
                  disabledDate={(date) => date && date.isBefore(dayjs().startOf("day"))}
                  onChange={(date) => {
                    setEditDate(date);
                    setEditTime(null);
                  }}
                />
              </div>
              <div>
                <Text strong>Horario disponible</Text>
                <Select
                  value={editTime}
                  placeholder={loadingSlots ? "Consultando..." : "Seleccioná un horario"}
                  size="large"
                  style={{ width: "100%", marginTop: 7 }}
                  suffixIcon={loadingSlots ? <Spin size="small" /> : <ClockCircleOutlined />}
                  loading={loadingSlots}
                  options={availableSlots.map((slot) => ({
                    value: String(slot.time).slice(0, 5),
                    label: String(slot.time).slice(0, 5),
                  }))}
                  onChange={setEditTime}
                />
              </div>
            </div>
          </Card>
        </Space>
      </Modal>
    </div>
  );
};

export default Reservations;
